export type OpsClientFreshnessResult =
  | "current"
  | "unavailable"
  | "reloading"
  | "stale_blocked";

export interface OpsClientFreshnessGate {
  check(): Promise<OpsClientFreshnessResult>;
}

interface OpsClientFreshnessInput {
  currentEntryPath: string;
  fetchIndexHtml: () => Promise<string>;
  reload: () => void;
  getReloadMarker: () => string | null;
  setReloadMarker: (value: string) => void;
  clearReloadMarker: () => void;
  now: () => number;
  cooldownMs: number;
  indexBaseUrl?: string;
}

const OPS_CLIENT_RELOAD_MARKER = "business-ops:client-reload-target";

/**
 * WHY [BOL-VOICE-CURRENT-CLIENT-001]: an Android tab can retain an old Vite
 * document while the self-hosted API moves forward. That mixed-version path
 * reproduced old Voice failures even though both the current Web and API were
 * green. Compare the loaded hashed entry with a fresh root document before
 * opening Voice, and reload at most once for that exact target. Never poll,
 * reload an active Voice turn, or loop when a proxy/browser still returns the
 * old document. Falsifier: c321 opens a c326 Voice socket, or one mismatch
 * causes two reloads.
 */
export class OpsClientFreshnessGuard implements OpsClientFreshnessGate {
  private readonly currentEntryPath: string | null;
  private inFlight: Promise<OpsClientFreshnessResult> | null = null;
  private lastCurrentAt = Number.NEGATIVE_INFINITY;
  private reloadRequested = false;

  constructor(private readonly input: OpsClientFreshnessInput) {
    this.currentEntryPath = normalizeEntryPath(input.currentEntryPath, input.indexBaseUrl);
  }

  check(): Promise<OpsClientFreshnessResult> {
    if (this.reloadRequested) return Promise.resolve("reloading");
    if (this.input.now() - this.lastCurrentAt < this.input.cooldownMs) {
      return Promise.resolve("current");
    }
    if (this.inFlight) return this.inFlight;
    const current = this.performCheck();
    this.inFlight = current;
    void current.finally(() => {
      if (this.inFlight === current) this.inFlight = null;
    });
    return current;
  }

  private async performCheck(): Promise<OpsClientFreshnessResult> {
    if (!this.currentEntryPath) return "unavailable";
    let html: string;
    try {
      html = await this.input.fetchIndexHtml();
    } catch {
      return "unavailable";
    }
    const latestEntryPath = extractOpsViteEntryPath(html, this.input.indexBaseUrl);
    if (!latestEntryPath) return "unavailable";
    if (latestEntryPath === this.currentEntryPath) {
      this.lastCurrentAt = this.input.now();
      try { this.input.clearReloadMarker(); } catch { /* storage can be unavailable */ }
      return "current";
    }
    let priorTarget: string | null = null;
    try { priorTarget = this.input.getReloadMarker(); } catch { /* storage can be unavailable */ }
    if (priorTarget === latestEntryPath) return "stale_blocked";
    try { this.input.setReloadMarker(latestEntryPath); } catch { /* reload remains bounded in memory */ }
    this.reloadRequested = true;
    try {
      this.input.reload();
      return "reloading";
    } catch {
      return "stale_blocked";
    }
  }
}

export function extractOpsViteEntryPath(html: string, baseUrl = "https://ops.invalid/"): string | null {
  const scriptTags = html.match(/<script\b[^>]*>/giu) ?? [];
  for (const tag of scriptTags) {
    if (!/\btype\s*=\s*(?:["']module["']|module)(?=\s|>)/iu.test(tag)) continue;
    const source = tag.match(/\bsrc\s*=\s*(?:"([^"]+)"|'([^']+)'|([^\s>]+))/iu);
    const candidate = source?.[1] ?? source?.[2] ?? source?.[3];
    if (!candidate) continue;
    const normalized = normalizeEntryPath(candidate, baseUrl);
    if (normalized) return normalized;
  }
  return null;
}

export function createBrowserOpsClientFreshnessGate(): OpsClientFreshnessGate | null {
  if (typeof window === "undefined" || typeof document === "undefined" || typeof fetch !== "function") {
    return null;
  }
  const entry = document.querySelector<HTMLScriptElement>('script[type="module"][src]')?.src;
  if (!entry) return null;
  const rootUrl = new URL("/", window.location.origin).href;
  return new OpsClientFreshnessGuard({
    currentEntryPath: entry,
    indexBaseUrl: rootUrl,
    fetchIndexHtml: async () => {
      const response = await fetch(rootUrl, {
        cache: "no-store",
        credentials: "same-origin",
        headers: { accept: "text/html" }
      });
      if (!response.ok) throw new Error("ops_client_freshness_http");
      return response.text();
    },
    reload: () => window.location.reload(),
    getReloadMarker: () => window.sessionStorage.getItem(OPS_CLIENT_RELOAD_MARKER),
    setReloadMarker: (value) => window.sessionStorage.setItem(OPS_CLIENT_RELOAD_MARKER, value),
    clearReloadMarker: () => window.sessionStorage.removeItem(OPS_CLIENT_RELOAD_MARKER),
    now: () => Date.now(),
    cooldownMs: 30_000
  });
}

function normalizeEntryPath(entry: string, baseUrl = "https://ops.invalid/"): string | null {
  try {
    return new URL(entry, baseUrl).pathname;
  } catch {
    return null;
  }
}
